import React, { useEffect } from "react"
import { Route, useHistory, useLocation } from "react-router-dom"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import "./App.css"

import { ApplicationErrorBoundary } from "components/Error/ApplicationErrorBoundary"
import { AppRoot } from "components/layout/AppRoot"
import { Loading } from "components/molecules/Loading"
import logger from "lib/logger"

import { removeAuthData } from "./actions/auth"
import { loadCurrentTeam } from "./actions/currentTeam"
import { loadProfile } from "./actions/profile"
import { loadTeams } from "./actions/teams"
import NotFound from "./components/Error/NotFound"
import { AppThemeProvider } from "./components/Theme/ThemeProvider"
import { AppHeader } from "./components/layout/AppHeader"
import { useTeamMember } from "./hooks"
import { usePathAndPermissions } from "./hooks/usePathAndPermissions"
import { setLang } from "./lib/i18n"
import { getServiceName, updateServiceByReferrer } from "./lib/service"
import { updateThemeByReferrer } from "./lib/theme"
import useCurrentTeam from "./recoils/atoms/currentTeam"
import useCurrentUserRole from "./recoils/atoms/currentUserRole"
import useProfile from "./recoils/atoms/profile"
import useTeams from "./recoils/atoms/teams"
import useAuth from "./recoils/atoms/useAuth"
import Router from "./router/Router"

function App() {
  const location = useLocation()
  const history = useHistory()
  const setAuth = useSetRecoilState(useAuth)
  const [profile, setProfile] = useRecoilState(useProfile)
  const setTeams = useSetRecoilState(useTeams)
  const currentUserRole = useRecoilValue(useCurrentUserRole)
  const [currentTeam, setCurrentTeam] = useRecoilState(useCurrentTeam)
  const { getTeamMembers } = useTeamMember()

  const { isPrivatePage, hasPermissionForPage, shouldRedirectProfilePage } =
    usePathAndPermissions(currentUserRole)

  const getUserProfiles = async () =>
    Promise.all([loadProfile(setAuth, setProfile), loadTeams()]).then(
      (res) => ({ auth: res[0], teamsState: res[1] }),
    )

  useEffect(() => {
    setLang(profile.locale)
    // 描画時の `location` ではなく、いまの `history.location` を書き戻す。
    // 子の effect（先に走る）が URL を書き換えていることがあるため。描画時の値を
    // 使うと、メールのリンクのトークンを消した URL に元のクエリを戻してしまう（#443 M1）。
    history.replace({
      pathname: history.location.pathname,
      hash: history.location.hash,
      search: history.location.search,
    })
  }, [profile])

  useEffect(() => {
    updateServiceByReferrer()
    updateThemeByReferrer()
  }, [])

  useEffect(() => {
    if (isPrivatePage) {
      getUserProfiles()
        .then(({ teamsState }) => {
          const teams = teamsState.list
          setTeams((teams) => ({ ...teams, ...teamsState }))
          loadCurrentTeam(teams, setCurrentTeam)
        })
        .catch((error) => {
          logger("Failed to load profiles", error)
          removeAuthData()
          history.push("/login")
        })
    }
  }, [isPrivatePage])

  useEffect(() => {
    if (!currentTeam.id) return
    getTeamMembers(getServiceName())
  }, [currentTeam])

  useEffect(() => {
    if (shouldRedirectProfilePage) {
      history.push(`/profile?ref=${location.pathname}`)
    }
  }, [shouldRedirectProfilePage])

  if (isPrivatePage && currentUserRole.teamId === "") {
    return (
      <AppThemeProvider>
        <Loading />
      </AppThemeProvider>
    )
  }

  if (isPrivatePage && !hasPermissionForPage) {
    return (
      <AppThemeProvider>
        <Route component={NotFound} />
      </AppThemeProvider>
    )
  }

  return (
    <ApplicationErrorBoundary>
      <AppThemeProvider>
        <AppRoot>
          <AppHeader />
          {/*
            ★ **`key` でチームごとにページを作り直しています（#430）。**

            `AppHeader.openCms` / `openCloud` は **SPA のナビゲーションを起こさずに**
            `setCurrentTeam` してから別プロダクトを新しいタブで開きます
            （`setCurrentTeam` の目的は、CMS / CLOUD と共有している Cookie
            `SpearlyTeamID` を書き換えることです）。そのため
            **「チームが変わるのに、開いているページが作り直されない」経路**が 1 本あり、
            マウント時のチームを握ったままの `useEffect` / `useMemo` が
            前のチームのデータを使い続けていました。実害は 3 件で、一番重いのは
            **Stripe の SetupIntent が前のチームに紐づく**（= 登録したカードが
            別チームのものになる）ことでした。

            **deps を 1 つずつ直す代わりにここで吸収しています。**
            呼び出し側が「チームを変えたら遷移する」ことを覚えている必要が無くなるので、
            **将来どこからチームが変わっても再マウントが構造的に保証されます。**

            `key` は **routed content だけ**に付けること。`AppHeader` はチーム選択 UI
            そのもので、作り直すと操作した瞬間に自分が消えます。

            初回ロードで二重マウントにならないのは、上の **2 つのゲートが対で**
            `currentTeam.id` の確定まで `<Router />` のマウントを遅らせているからです:

              1. `isPrivatePage && currentUserRole.teamId === ""` → `<Loading />`
              2. `isPrivatePage && !hasPermissionForPage` → `<NotFound />`
                 （`hasPermissionForPage` は `useState(false)` 始まりで、
                  `currentUserRole.teamId` が埋まるまで true にならない）

            **片方だけ外しても二重マウントは起きません。両方外すと起きます**
            （実測: 1 だけ外す→1 回 / 2 だけ外す→1 回 / **両方外す→2 回**）。
            **対になった防御なので、片方を消すときはもう片方が残っているかを確かめること。**
            `src/__tests__/teamChangeRemount.test.tsx` が固定しています。
          */}
          <Router key={currentTeam.id} />
        </AppRoot>
      </AppThemeProvider>
    </ApplicationErrorBoundary>
  )
}

export default App
